import { Button, Divider, Form, Input, Select, Space, Typography } from 'antd'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { CustomSelect, Icon } from '../../../components'
import { countryCodes, translations, usStates } from '../../../constants'
import { trimFields } from '../../../utils'

const { Title } = Typography

const UsForm = ({
  hideModal,
  isFormNeedPaymentMethods = true,
  locale = 'us',
  onSubmit,
  selectedStore,
}) => {
  const initialValues = { accountType: 'CHECKING', store_id: selectedStore.id }
  const [form] = Form.useForm()
  const [isModalLoading, setModalLoading] = useState(false)
  const selectedCountryCode = Form.useWatch('country', form)

  const accountTypeOptions = [
    { label: 'CHECKING', value: 'CHECKING' },
    { label: 'SAVINGS', value: 'SAVINGS' },
  ]

  const handleSubmit = () => {
    const isFormUpdated = form.isFieldsTouched()

    if (isFormUpdated) {
      setModalLoading(true)

      form
        .validateFields()
        .then(() => {
          const fields = {
            ...trimFields(form.getFieldsValue(['store_id', 'abartn', 'accountNumber', 'accountType', 'reporting_email', 'legal_entity'])),
            address: {
              state: null,
              ...trimFields(form.getFieldsValue([
                'country',
                'city',
                'postCode',
                'firstLine',
              ])),
            },
            store_id: selectedStore.id,
          }

          if (fields.address.country === 'US') {
            fields.address = {
              ...fields.address,
              ...form.getFieldsValue(['state']),
            }
          }

          onSubmit({
            country: 'us',
            requiredFieldsValue: fields,
            setModalLoading,
          })
        })
    }
  }

  return (
    <Form
      data-test="bank-details-form"
      data-testid="usForm"
      form={form}
      initialValues={initialValues}
      layout="vertical"
      onFinish={handleSubmit}
    >
      <Form.Item
        className="bankDetailsAbartn"
        label={translations[locale].abartn}
        name="abartn"
        rules={[
          {
            message: 'Please make sure your ACH routing number is correct!',
            required: true,
          },
          {
            max: 9,
            message: 'Please add a 9-digit ACH routing number!',
          },
          {
            message: 'Please add a 9-digit ACH routing number!',
            min: 9,
          },
          {
            message: 'ACH routing number should contain only numbers!',
            pattern: /^[0-9]+$/,
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        className="bankDetailsAccountNumber"
        label={translations[locale].accountNumber}
        name="accountNumber"
        rules={[
          {
            message: 'Please enter your account number!',
            required: true,
          },
          {
            message: 'Please add a 6-digit account number!',
            min: 6,
          },
          {
            message: 'Account number should contain only numbers!',
            pattern: /^[0-9]+$/,
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        className="bankDetailsAccountType"
        label={translations[locale].accountType}
        name="accountType"
        rules={[
          {
            message: 'Please provide an account type!',
            required: true,
          },
        ]}
      >
        <Select options={accountTypeOptions} />
      </Form.Item>

      <Divider />

      <Title level={5}>{translations[locale].address}</Title>

      <Form.Item
        className="bankDetailsCountry"
        initialValue="US"
        label={translations[locale].country}
        name="country"
        rules={[
          {
            message: 'Please provide a country!',
            required: true,
          },
        ]}
      >
        <CustomSelect entries={countryCodes} />
      </Form.Item>

      {selectedCountryCode === 'US' &&
        <Form.Item
          className="bankDetailsState"
          data-testid="state"
          initialValue={usStates[0].iso}
          label={translations[locale].state}
          name="state"
          rules={[
            {
              message: 'Please provide a state!',
              required: true,
            },
          ]}
        >
          <CustomSelect entries={usStates} />
        </Form.Item>
      }

      <Form.Item
        className="bankDetailsCity"
        label={translations[locale].city}
        name="city"
        rules={[
          {
            message: 'Please provide a city!',
            required: true,
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        className="bankDetailsPostCode"
        label={translations[locale].postCode}
        name="postCode"
        rules={[
          {
            message: 'Please provide a post code!',
            required: true,
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        className="bankDetailsAddress"
        label={translations[locale].address}
        name="firstLine"
        rules={[
          {
            message: 'Please provide address!',
            required: true,
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label={translations[locale].legalEntity}
        name="legal_entity"
        rules={[{ message: 'Please enter the Legal Entity!', required: true }]}
        tooltip={{ icon: <Icon type="infoCircle" />, title: translations[locale].legalEntityTooltip }}
      >
        <Input />
      </Form.Item>

      <Form.Item
        label={translations[locale].reportingEmail}
        name="reporting_email"
        rules={[
          {
            message: 'The input is not valid email!',
            type: 'email',
          },
          {
            message: 'Please provide a valid reporting email address!',
            required: true,
          },
        ]}
      >
        <Input />
      </Form.Item>

      <Divider />

      <Space
        size="middle"
        style={{
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        {isFormNeedPaymentMethods && <Button className="bankDetailsCancel" onClick={hideModal}>Cancel</Button>}
        <Button className="bankDetailsSubmit" htmlType="submit" loading={isModalLoading} name="submitBtnSelenium" type="primary">{translations[locale].submit}</Button>
      </Space>
    </Form>
  )
}

UsForm.propTypes = {
  hideModal: PropTypes.func,
  isFormNeedPaymentMethods: PropTypes.bool,
  locale: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  selectedStore: PropTypes.object.isRequired,
}

export default UsForm
